<template>

 
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
      <ion-toolbar class="breadcrumb-bar" v-if="activeSection">
        <div class="breadcrumb">
          <span class="crumb-link" @click="activeSection = null">Studio</span>
          <span class="crumb-sep">›</span>
          <span class="crumb-current">{{ activeSection.label }}</span>
        </div>
      </ion-toolbar>
    </ion-header>

      <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>

    <ion-content :fullscreen="true">
      <Transition name="slide-up" mode="out-in">

        <!-- Top-level tile grid -->
        <div v-if="!activeSection" class="tile-grid" key="tiles">

          <!-- Opening Mastery — full width -->
          <div class="tile tile-purple tile-full" @click="openSection(sections.openingMastery)">
            <ion-icon :icon="trophyOutline" class="tile-icon" aria-hidden="true" />
            <div class="tile-text">
              <span class="tile-label">{{ t("Opening Mastery") }}</span>
              <span class="tile-sub">{{ t("Explorer free · Full access with subscription") }}</span>
            </div>
          </div>

          <!-- Repertoires & ChessVis AI -->
           <!-- make repertoires full width until done the work to add AI -->
          <div class="tile tile-purple tile-full" @click="openSection(sections.repertoires)">
            <ion-icon :icon="bookOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Repertoires") }}</span>
            <span class="tile-sub-badge">{{ t("Subscriber") }}</span>
          </div>

          <!-- <div class="tile tile-ai" @click="openSection(sections.ai)">
            <ion-icon :icon="sparklesOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">ChessVis AI</span>
            <span class="tile-sub-badge">{{ t("Subscriber") }}</span>
          </div> -->

          <!-- Legacy — muted row -->
          <div class="tile tile-legacy" @click="openSection(sections.legacyOpening)">
            <ion-icon :icon="timeOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Legacy Opening") }}</span>
          </div>

          <div class="tile tile-legacy" @click="openSection(sections.legacyRepertoires)">
            <ion-icon :icon="archiveOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Legacy Repertoires") }}</span>
          </div>

        </div>

        <!-- Drill-down list -->
        <div v-else class="drill-down" key="list">
          <ion-list :inset="true">

            <ion-item-group v-for="group in activeSection.groups" :key="group.id">
              <ion-item-divider v-if="group.label">
                <ion-label>{{ group.label }}</ion-label>
              </ion-item-divider>

              <ion-item
                v-for="item in group.items"
                :key="item.id"
                :button="!item.locked"
                :detail="!item.locked"
                @click="!item.locked && navigateTo(item)"
                :class="{ 'item-locked': item.locked }"
              >
                <div class="item-icon-wrap" slot="start" :class="{ 'icon-locked': item.locked }">
                  <ion-icon :icon="item.icon" class="item-icon" aria-hidden="true" />
                </div>
                <ion-label>
                  <h2>{{ item.label }}</h2>
                  <p v-if="item.description">{{ item.description }}</p>
                </ion-label>
                <ion-badge v-if="item.free" slot="end" class="free-badge">{{ t("Free") }}</ion-badge>
                <ion-icon
                  v-if="item.locked"
                  :icon="lockClosedOutline"
                  slot="end"
                  class="lock-icon"
                  aria-hidden="true"
                />
              </ion-item>

            </ion-item-group>

          </ion-list>
        </div>

      </Transition>
    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonButtons, IonBackButton, IonContent,
  IonIcon, IonList, IonItem, IonItemGroup,
  IonItemDivider, IonLabel, IonBadge,
} from '@ionic/vue'
import {
  trophyOutline, bookOutline, sparklesOutline,
  timeOutline, archiveOutline, lockClosedOutline,
  searchOutline, downloadOutline, analyticsOutline,
  constructOutline, gitBranchOutline, listOutline,
  shareOutline, cloudDownloadOutline, statsChartOutline,
  chatbubbleOutline, readerOutline,
} from 'ionicons/icons'
import BottomNav from '@/components/BottomNav.vue'
import { useConnectionStore } from '@/stores/connectionStore';
import AlertReplacement from '@/components/AlertReplacement.vue'
import { t } from '@/composables/translateText.js'

const emit = defineEmits(['menu-item-click'])

const connectionStore = useConnectionStore()
const isSubscriber = connectionStore.getIsSubscriber

const alertInfo = ref({})
const alertTrigger = ref(0)

interface MenuItem {
  id: string
  label: string
  description?: string
  icon: string
  locked: boolean
  free?: boolean
  route: string
}

interface MenuGroup {
  id: string
  label?: string
  items: MenuItem[]
}

interface Section {
  id: string
  label: string
  groups: MenuGroup[]
}

const sections = {

  openingMastery: {
    id: 'opening-mastery',
    label: t('Opening Mastery'),
    groups: [
      {
        id: 'free',
        label: t('Free'),
        items: [
          {
            id: 'explorer',
            label: t('Explorer'),
            description: t('Data-driven opening explorer — 200M+ peer-level games'),
            icon: searchOutline,
            locked: false,
            free: true,
            route: 'explorer',
          },
        ],
      },
      {
        id: 'subscriber',
        label: t('Subscriber'),
        items: [
            {
            id: 'aiexplorer',
            label: 'AI Opening Explorer',
            description: t('Data-driven opening explorer - with AI analysis'),
            icon: searchOutline,
            locked: false,
            free: true,
            route: 'ai_explorer',
          },  
          {
            id: 'download-games',
            label: t('Download Your Games'),
            description: t('Import your games from chess.com and lichess.org'),
            icon: downloadOutline,
            locked: false,
            route: 'download',
          },
          {
            id: 'analyze-games',
            label: t('Analyze Your Games'),
            description: t('Run your games through the Opening Explorer'),
            icon: analyticsOutline,
            locked: false,
            route: 'exploreUserGames',
          },
        ],
      },
    ],
  },

  repertoires: {
    id: 'repertoires',
    label: t('Repertoires'),
    groups: [
      {
        id: 'build',
        label: t('Build'),
        items: [
          {
            id: 'manager',
            label: t('Manager'),
            description: t('Create and manage your repertoire library'),
            icon: listOutline,
            locked: false,
            route: 'repmanager',
          },
          {
            id: 'builder',
            label: t('Builder'),
            description: t('Build your opening lines move by move'),
            icon: constructOutline,
            locked: false,
            route: 'repbuilder',
          },
          {
            id: 'tree-viewer',
            label: t('Tree Viewer'),
            description: t('See your entire repertoire as a branching tree'),
            icon: gitBranchOutline,
            locked: false,
            route: 'reptree',
          },
        ],
      },
      {
        id: 'train',
        label: t('Train'),
        items: [
          {
            id: 'drill',
            label: t('Drill'),
            description: t('Daily spaced repetition of your repertoire moves'),
            icon: readerOutline,
            locked: false,
            route: 'repdrill',
          },
          {
            id: 'practice',
            label: t('Practice'),
            description: t('Extend play into the middle game — find your weak branches'),
            icon: statsChartOutline,
            locked: false,
            route: 'reppractice',
          },
        ],
      },
      {
        id: 'transfer',
        label: t('Transfer'),
        items: [
          {
            id: 'export',
            label: t('Export'),
            description: t('Export your repertoire for use in other tools'),
            icon: shareOutline,
            locked: false,
            route: 'repexport',
          },
        ],
      },
    ],
  },

  // ai: {
  //   id: 'ai',
  //   label: 'ChessVis AI',
  //   groups: [
  //     {
  //       id: 'ai-main',
  //       items: [
  //         {
  //           id: 'ai-coach',
  //           label: t('ChessVis AI Coach'),
  //           description: t('Analyze your games and get coaching feedback'),
  //           icon: chatbubbleOutline,
  //           locked: false,
  //           route: '/studio/ai',
  //         },
  //       ],
  //     },
  //   ],
  // },

  legacyOpening: {
    id: 'legacy-opening',
    label: t('Legacy Opening'),
    groups: [
      {
        id: 'legacy-opening-items',
        items: [
          {
            id: 'win-loss',
            label: t('Opening Explorer — Win/Loss'),
            description: t('Classic win/loss opening statistics'),
            icon: statsChartOutline,
            locked: false,
            free : true,
            route: 'legacy_opening_winloss',
          },
          {
            id: 'play-collection',
            label: t('Play Against Game Collection'),
            description: t('Play against moves from real game collections'),
            icon: gitBranchOutline,
            locked: false,
            free : true,
            route: 'legacy_play_collection',
          },
          {
            id: 'slice-dice',
            label: t('Opening Explorer — Slice/Dice'),
            description: t('Slice and dice opening data by filters'),
            icon: analyticsOutline,
            locked: false,
            route: 'legacy_opening_slicedice',
          },
          {
            id: 'download-legacy',
            label: t('Download Your Games'),
            description: t('Download games for legacy analysis'),
            icon: cloudDownloadOutline,
            locked: false,
            route: 'download',
          },
          {
            id: 'master-explorer',
            label: t('Master Player Explorer'),
            description: t('Explore openings from master-level games'),
            icon: searchOutline,
            locked: false,
            route: 'legacy_master_player',
          },
        ],
      },
    ],
  },

  legacyRepertoires: {
    id: 'legacy-repertoires',
    label: t('Legacy Repertoires'),
    groups: [
      {
        id: 'legacy-rep-items',
        items: [
          {
            id: 'legacy-manager',
            label: t('Repertoire Manager'),
            description: t('Manage your classic repertoires'),
            icon: listOutline,
            locked: false,
            route: 'legacy_repertoire_manager',
          },
          {
            id: 'legacy-builder',
            label: t('Repertoire Builder'),
            description: t('Classic repertoire building tool'),
            icon: constructOutline,
            locked: false,
            route: 'legacy_repertoire_builder',
          },
          {
            id: 'legacy-tree',
            label: t('Repertoire Tree'),
            description: t('Classic tree view of your repertoire'),
            icon: gitBranchOutline,
            locked: false,
            route: 'legacy_repertoire_tree',
          },
          {
            id: 'legacy-practice',
            label: t('Repertoire Practice'),
            description: t('Classic repertoire practice mode'),
            icon: statsChartOutline,
            locked: false,
            route: 'legacy_repertoire_practice',
          },
          {
            id: 'legacy-export',
            label: t('Repertoire Export'),
            description: t('Export your classic repertoire'),
            icon: shareOutline,
            locked: false,
            route: 'legacy_repertoire_export',
          },
          {
            id: 'legacy-import',
            label: t('Repertoire Import (Web)'),
            description: t('Import repertoires via web'),
            icon: cloudDownloadOutline,
            locked: false,
            route: 'legacy_repertoire_import',
          },
        ],
      },
    ],
  },

}

const activeSection = ref<Section | null>(null)

function openSection(section: Section) {
  activeSection.value = section
}

// is there a secion id coming back

const sectionInUse = connectionStore.getMenuSectionInUse
console.log("section in use in studio",sectionInUse)
if (sectionInUse != null && sectionInUse != "") {
  switch (sectionInUse) {
    case "opening-mastery" :
      activeSection.value = sections.openingMastery
      break
    case "repertoires" :
      activeSection.value = sections.repertoires
      break
    case "ai" :
      activeSection.value = sections.ai
      break
    case "legacy-opening" :
      activeSection.value = sections.legacyOpening
      break
   case "legacy-repertoires" :
      activeSection.value = sections.legacyRepertoires
      break
  }
}
console.log("active section",activeSection.value)

  function setupAlert(header:string,message:string,type:string,task:string) {
    alertInfo.value = { header : header, message : message, type : type, task : task  }
    alertTrigger.value++
  }

  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task)

    if (response == "no")
      return

    emit("menu-item-click","subscribe_blurb","Subscriber Benefits","program")
  }
  

function navigateTo(item: MenuItem) {
    console.log("studio item",item)

    // check for subscriber
    if (!item.free && isSubscriber != 1) {
              setupAlert(t("Become A Subscriber?"),t("That program is reserved for subscribers. Would you like to read about the benefits of subscribing?"),"yesno","subscribe")
      return
    }
    // set the return values for clicking on the done button
    connectionStore.setMenuInUse("studio")
    connectionStore.setMenuSectionInUse(activeSection.value.id)

    onClick(item.route,item.label,"FROM_NEW_MENU")
  }

  function onClick(path : string,label : string,whichMenu : string) {
    console.log("Landing page CLICK ",path,label)
    connectionStore.setIsUsingNewMenus(true)

    emit("menu-item-click",path,label,whichMenu)
  }

</script>

<style scoped>
ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-back-button { --color: #AFA9EC; }

ion-title {
  font-size: 17px;
  font-weight: 500;
}

.breadcrumb-bar {
  --background: #0f0f22;
  --min-height: 32px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  font-size: 16px;
}

.crumb-link    { color: #AFA9EC; cursor: pointer; }
.crumb-sep     { color: #444466; }
.crumb-current { color: #888880; }

ion-content { --background: #1a1a2e; }

/* ── Tile grid ── */
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 18px;
  padding: 24px 16px 20px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.tile:active {
  opacity: 0.85;
  transform: scale(0.97);
}

/* Full width tile */
.tile-full {
  grid-column: 1 / -1;
  flex-direction: row;
  justify-content: flex-start;
  padding: 20px;
  gap: 16px;
  flex-direction: column;

}

.tile-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

/* Purple tiles */
.tile-purple { background: #3C3489; }
.tile-purple .tile-icon  { color: #CECBF6; }
.tile-purple .tile-label { color: #CECBF6; }
.tile-purple .tile-sub   { color: #AFA9EC; font-size: 11px; line-height: 1.3; }

/* AI tile */
.tile-ai { background: #2d2060; }
.tile-ai .tile-icon  { color: #c4bfff; }
.tile-ai .tile-label { color: #c4bfff; }

/* Legacy tiles — muted */
.tile-legacy {
  background: #1e1e2e;
  border: 0.5px solid #2a2a4a;
  opacity: 0.8;
}
.tile-legacy .tile-icon  { color: #666680; }
.tile-legacy .tile-label { color: #888890; }

.tile-icon  { font-size: 30px; flex-shrink: 0; }
.tile-full .tile-icon { font-size: 26px; }

.tile-label {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
}

.tile-sub-badge {
  font-size: 10px;
  font-weight: 500;
  color: #AFA9EC;
  background: rgba(127, 119, 221, 0.2);
  padding: 2px 8px;
  border-radius: 99px;
}

/* ── Drill-down list ── */
.drill-down { padding-top: 8px; }

ion-list { --background: transparent; background: transparent; }

ion-item-divider {
  --background: #0f0f22;
  --color: #7F77DD;
  --padding-start: 16px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-height: 32px;
}

ion-item {
  --background: #1e1e35;
  --background-activated: #2a2a45;
  --color: #e0e0f0;
  --border-color: #2a2a4a;
  --detail-icon-color: #7F77DD;
  --detail-icon-opacity: 1;
  --padding-start: 12px;
  --inner-padding-end: 12px;
  --min-height: 62px;
}

ion-item h2 {
  font-size: 15px;
  font-weight: 400;
  color: #e0e0f0;
  margin-bottom: 3px;
}

ion-item p {
  font-size: 12px;
  color: #888880;
  line-height: 1.4;
  white-space: normal;
}

.item-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #3C3489;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
}

.item-icon-wrap.icon-locked { background: #1e1e2e; }

.item-icon { font-size: 19px; color: #CECBF6; }

.item-locked h2 { color: #555570; }
.item-locked p  { color: #444455; }
.item-locked .item-icon { color: #444460; }

.free-badge {
  --background: #085041;
  --color: #9FE1CB;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 99px;
}

.lock-icon {
  font-size: 16px;
  color: #444460;
}

/* ── Transition ── */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.slide-up-enter-from { opacity: 0; transform: translateY(12px); }
.slide-up-leave-to   { opacity: 0; transform: translateY(-8px); }
</style>
